import React, { useState, useEffect, useContext } from "react";
import moment from "moment";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { DataGrid } from "@mui/x-data-grid";
import { Tabs, Tab } from "@mui/material";
import { generateExcelFile } from "../../../shared/components/excelUtils/exportExcel";
import ArticleIcon from "@mui/icons-material/Article";
import { AuthContext } from "../../../shared/context/auth-context";
import { useLoading } from "../../../shared/context/LoadingContext";
import {
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Paper,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import "./ModalHistoricoMovimentos.scss";

const MOTIVOS_DESCARTE = {
  EXPIRADO: "Produto expirado/vencido",
  DANIFICADO: "Produto danificado",
  CONTAMINADO: "Produto contaminado",
  RECOLHIDO_FABRICANTE: "Recolhido pelo fabricante",
  ENTRADA_INCORRETA: "Entrada incorreta no sistema",
  PERDIDO: "Produto perdido/extraviado",
  QUEBRADO: "Produto quebrado",
  OUTRO: "Outro motivo",
};

const DetalhesDescarteDialog = ({ open, handleClose, detalhes }) => {
  if (!detalhes) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: "#ffebee",
          color: "#d32f2f",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <RemoveCircleOutlineIcon />
        Detalhes do Descarte
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Produto
            </Typography>
            <Typography variant="body1" gutterBottom>
              {detalhes.produto}
            </Typography>
            <Typography variant="subtitle2" color="textSecondary">
              Lote
            </Typography>
            <Typography variant="body1" gutterBottom>
              {detalhes.lote}
            </Typography>
            <Typography variant="subtitle2" color="textSecondary">
              Quantidade
            </Typography>
            <Typography variant="body1" gutterBottom>
              {detalhes.quantidade}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Data do Descarte
            </Typography>
            <Typography variant="body1" gutterBottom>
              {detalhes.data}
            </Typography>
            <Typography variant="subtitle2" color="textSecondary">
              Autorizado Por
            </Typography>
            <Typography variant="body1" gutterBottom>
              {detalhes.responsavel}
            </Typography>
            <Typography variant="subtitle2" color="textSecondary">
              Motivo
            </Typography>
            <Typography variant="body1" gutterBottom>
              {MOTIVOS_DESCARTE[detalhes.motivoDescarte] ||
                detalhes.motivoDescarte}
            </Typography>
          </Grid>
          {detalhes.observacoes && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Observações
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f8f8f8" }}>
                <Typography variant="body2">{detalhes.observacoes}</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function ModalHistoricoMovimentos({
  open,
  handleClose,
  sendRequest,
  clinicaId,
}) {
  const { startLoading, stopLoading } = useLoading();
  const auth = useContext(AuthContext);

  const [activeTab, setActiveTab] = useState(0);
  const [dataInicial, setDataInicial] = useState(
    moment().subtract(1, "months").startOf("day"),
  );
  const [dataFinal, setDataFinal] = useState(moment().endOf("day"));

  const [rowsEntradas, setRowsEntradas] = useState([]);
  const [rowsSaidas, setRowsSaidas] = useState([]);
  const [rowsTransferencias, setRowsTransferencias] = useState([]);

  const [detalhesDescarteOpen, setDetalhesDescarteOpen] = useState(false);
  const [descarteAtual, setDescarteAtual] = useState(null);

  useEffect(() => {
    if (open) fetchMovimentos();
  }, [open, clinicaId, auth.token]);

  const fetchMovimentos = async () => {
    startLoading();
    try {
      const formData = new FormData();
      formData.append("dataInicio", dataInicial.toISOString());
      formData.append("dataFim", dataFinal.toISOString());

      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/stocks/getMovimentosByClinica/${clinicaId}`,
        "PATCH",
        formData,
        { Authorization: "Bearer " + auth.token },
      );

      const movimentos = responseData.movimentos;

      // Entradas
      setRowsEntradas(
        movimentos
          .filter((mv) => mv.tipoMovimento === "entrada")
          .map((mv) => ({
            id: mv._id,
            data: moment(mv.data).format("DD-MM-YYYY HH:mm"),
            produto: mv.produto?.nome || "N/A",
            lote: mv.lote,
            qtdAdicionada: mv.quantidade,
            respMovimento:
              mv.responsavelMovimento?.responsavel?.name ||
              mv.responsavelMovimento?.responsavel?.nome ||
              "N/A",
            localizacaoDestino: mv.localizacaoDestino || null,
          }))
          .sort(
            (a, b) =>
              moment(b.data, "DD-MM-YYYY HH:mm") -
              moment(a.data, "DD-MM-YYYY HH:mm"),
          ),
      );

      // Saídas
      setRowsSaidas(
        movimentos
          .filter((mv) => mv.tipoMovimento === "saida")
          .map((mv) => ({
            id: mv._id,
            data: moment(mv.data).format("DD-MM-YYYY HH:mm"),
            produto: mv.produto?.nome || "N/A",
            lote: mv.lote,
            qtdRetirada: mv.quantidade,
            respMovimento:
              mv.responsavelMovimento?.responsavel?.name ||
              mv.responsavelMovimento?.responsavel?.nome ||
              "N/A",
            beneficiario: mv.beneficiario?.responsavel
              ? mv.beneficiario.responsavel.name ||
                mv.beneficiario.responsavel.nome
              : "N/A",
            utilizador: mv.utilizador ? mv.utilizador.name : "N/A",
            isDescarte: !!mv.descarteId,
            motivoDescarte: mv.descarteId?.motivo,
            observacoesDescarte: mv.descarteId?.observacoes,
            localizacaoOrigem: mv.localizacaoOrigem || null,
          }))
          .sort(
            (a, b) =>
              moment(b.data, "DD-MM-YYYY HH:mm") -
              moment(a.data, "DD-MM-YYYY HH:mm"),
          ),
      );

      // Transferências
      setRowsTransferencias(
        movimentos
          .filter((mv) => mv.tipoMovimento === "transferencia")
          .map((mv) => ({
            id: mv._id,
            data: moment(mv.data).format("DD-MM-YYYY HH:mm"),
            produto: mv.produto?.nome || "N/A",
            lote: mv.lote,
            quantidade: mv.quantidade,
            respMovimento:
              mv.responsavelMovimento?.responsavel?.name ||
              mv.responsavelMovimento?.responsavel?.nome ||
              "N/A",
            localizacaoOrigem: mv.localizacaoOrigem || null,
            localizacaoDestino: mv.localizacaoDestino || null,
          }))
          .sort(
            (a, b) =>
              moment(b.data, "DD-MM-YYYY HH:mm") -
              moment(a.data, "DD-MM-YYYY HH:mm"),
          ),
      );
    } catch (err) {
      console.error("Erro ao buscar movimentos:", err);
    } finally {
      stopLoading();
    }
  };

  const handleVerDetalhesDescarte = (row) => {
    setDescarteAtual({
      produto: row.produto,
      lote: row.lote,
      quantidade: row.qtdRetirada,
      data: row.data,
      responsavel: row.respMovimento,
      motivoDescarte: row.motivoDescarte,
      observacoes: row.observacoesDescarte,
    });
    setDetalhesDescarteOpen(true);
  };

  const formatLocalizacao = (loc) => {
    if (!loc) return "N/A";
    const codigoArmazem = loc.armazem?.codigo || "";
    const codigoLoc = loc.codigo || "";
    const nomeLoc = loc.nome || "";
    if (!codigoArmazem && !codigoLoc) return "N/A";
    return `${codigoArmazem}/${codigoLoc} — ${nomeLoc}`;
  };

  // Colunas entradas
  const columnsEntradas = [
    {
      field: "data",
      headerName: "Data",
      flex: 1.5,
      valueGetter: (params) =>
        moment(params.value, "DD-MM-YYYY HH:mm").format("DD/MM/YYYY HH:mm"),
    },
    {
      field: "produto",
      headerName: "Produto",
      flex: 3,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "lote",
      headerName: "Lote",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "qtdAdicionada",
      headerName: "Qtd Adicionada",
      flex: 1,
      type: "number",
    },
    {
      field: "localizacaoDestino",
      headerName: "Localização",
      flex: 2,
      renderCell: (params) => <span>{formatLocalizacao(params.value)}</span>,
    },
    { field: "respMovimento", headerName: "Adicionada por", flex: 2 },
  ];

  // Colunas saídas
  const columnsSaidas = [
    {
      field: "data",
      headerName: "Data",
      flex: 1.5,
      valueGetter: (params) =>
        moment(params.value, "DD-MM-YYYY HH:mm").format("DD/MM/YYYY HH:mm"),
    },
    {
      field: "produto",
      headerName: "Produto",
      flex: 3,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    { field: "lote", headerName: "Lote", flex: 1 },
    { field: "qtdRetirada", headerName: "Quantidade", flex: 1, type: "number" },
    {
      field: "localizacaoOrigem",
      headerName: "Localização",
      flex: 2,
      renderCell: (params) => <span>{formatLocalizacao(params.value)}</span>,
    },
    {
      field: "isDescarte",
      headerName: "Tipo",
      flex: 1.5,
      renderCell: (params) => {
        if (params.value === true) {
          return (
            <Chip
              icon={<RemoveCircleOutlineIcon />}
              label="Descarte"
              color="error"
              variant="outlined"
              size="small"
            />
          );
        }
        return <span>Retirada</span>;
      },
    },
    {
      field: "acao",
      headerName: " ",
      width: 50,
      sortable: false,
      renderCell: (params) => {
        if (params.row.isDescarte) {
          return (
            <Tooltip title="Ver detalhes do descarte">
              <InfoOutlinedIcon
                color="error"
                style={{ cursor: "pointer" }}
                onClick={() => handleVerDetalhesDescarte(params.row)}
              />
            </Tooltip>
          );
        }
        return null;
      },
    },
    { field: "respMovimento", headerName: "Retirada por", flex: 2 },
    { field: "beneficiario", headerName: "Beneficiário", flex: 2 },
  ];

  // Colunas transferências
  const columnsTransferencias = [
    {
      field: "data",
      headerName: "Data",
      flex: 1.5,
      valueGetter: (params) =>
        moment(params.value, "DD-MM-YYYY HH:mm").format("DD/MM/YYYY HH:mm"),
    },
    {
      field: "produto",
      headerName: "Produto",
      flex: 3,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    { field: "lote", headerName: "Lote", flex: 1 },
    { field: "quantidade", headerName: "Quantidade", flex: 1, type: "number" },
    {
      field: "localizacaoOrigem",
      headerName: "Origem",
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={formatLocalizacao(params.value)}>
          <span>{formatLocalizacao(params.value)}</span>
        </Tooltip>
      ),
    },
    {
      field: "localizacaoDestino",
      headerName: "Destino",
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={formatLocalizacao(params.value)}>
          <span style={{ color: "#22c55e", fontWeight: 500 }}>
            {formatLocalizacao(params.value)}
          </span>
        </Tooltip>
      ),
    },
    { field: "respMovimento", headerName: "Responsável", flex: 2 },
  ];

  const handleExportar = () => {
    if (activeTab === 0) {
      const sheetData = rowsEntradas.map((r) => ({
        Data: r.data,
        Produto: r.produto,
        Lote: r.lote,
        Quantidade_Adicionada: r.qtdAdicionada,
        Localização: r.localizacaoDestino
          ? formatLocalizacao(r.localizacaoDestino)
          : "N/A",
        Adicionado_Por: r.respMovimento,
      }));
      generateExcelFile(
        `Historico_Entradas_${moment().format("YYYY-MM-DD")}.xlsx`,
        "Entradas de Stock",
        sheetData,
      );
    } else if (activeTab === 1) {
      const sheetData = rowsSaidas.map((r) => ({
        Data: r.data,
        Produto: r.produto,
        Lote: r.lote,
        Quantidade_Retirada: r.qtdRetirada,
        Localização: r.localizacaoOrigem
          ? formatLocalizacao(r.localizacaoOrigem)
          : "N/A",
        Tipo: r.isDescarte ? "Descarte" : "Retirada Normal",
        Motivo_Descarte: r.isDescarte
          ? MOTIVOS_DESCARTE[r.motivoDescarte] || r.motivoDescarte
          : "",
        Observacoes: r.observacoesDescarte || "",
        Retirado_Por: r.respMovimento,
        Beneficiario: r.beneficiario,
      }));
      generateExcelFile(
        `Historico_Retiradas_${moment().format("YYYY-MM-DD")}.xlsx`,
        "Retiradas de Stock",
        sheetData,
      );
    } else {
      const sheetData = rowsTransferencias.map((r) => ({
        Data: r.data,
        Produto: r.produto,
        Lote: r.lote,
        Quantidade: r.quantidade,
        Origem: r.localizacaoOrigem
          ? formatLocalizacao(r.localizacaoOrigem)
          : "N/A",
        Destino: r.localizacaoDestino
          ? formatLocalizacao(r.localizacaoDestino)
          : "N/A",
        Responsável: r.respMovimento,
      }));
      generateExcelFile(
        `Historico_Transferencias_${moment().format("YYYY-MM-DD")}.xlsx`,
        "Transferências de Stock",
        sheetData,
      );
    }
  };

  const currentRows =
    activeTab === 0
      ? rowsEntradas
      : activeTab === 1
        ? rowsSaidas
        : rowsTransferencias;

  const currentColumns =
    activeTab === 0
      ? columnsEntradas
      : activeTab === 1
        ? columnsSaidas
        : columnsTransferencias;

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box
          className="fluxo-atendimento__modal__box"
          id="historicoMovimentosModal__modal"
        >
          <h1 className="fluxo-atendimento__modal__titulo">
            Histórico de Movimentos
          </h1>

          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            className="historicoMovimentos__tabs"
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label={`Entradas (${rowsEntradas.length})`} />
            <Tab label={`Retiradas (${rowsSaidas.length})`} />
            <Tab
              icon={<SwapHorizIcon fontSize="small" />}
              iconPosition="start"
              label={`Transferências (${rowsTransferencias.length})`}
            />
          </Tabs>

          <div className="historico__dataFilter__container">
            <div className="historico__dataFilter__container__datas">
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DesktopDatePicker
                  className="valorDataCaixa__fields--field muiDatePicker"
                  label="Data Inicial*"
                  inputFormat="DD-MM-YYYY"
                  renderInput={(params) => <TextField {...params} />}
                  onChange={(value) => setDataInicial(value)}
                  value={dataInicial}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DesktopDatePicker
                  className="valorDataCaixa__fields--field muiDatePicker"
                  label="Data Final*"
                  inputFormat="DD-MM-YYYY"
                  renderInput={(params) => <TextField {...params} />}
                  onChange={(value) => setDataFinal(value)}
                  value={dataFinal}
                />
              </LocalizationProvider>
              <span
                onClick={fetchMovimentos}
                className="relatorio__filtros__container__btn relatorio__filtros__container__btn--blue"
              >
                Filtrar
              </span>
            </div>

            <Button
              variant="outlined"
              color="success"
              startIcon={<ArticleIcon size={18} />}
              onClick={handleExportar}
            >
              Exportar Excel
            </Button>
          </div>

          <div style={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={currentRows}
              columns={currentColumns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              getRowClassName={(params) =>
                params.row.isDescarte ? "row-descarte" : ""
              }
            />
          </div>

          <div className="fluxo-atendimento__modal__bottom_btns">
            <span className="cancel-btn" onClick={handleClose}>
              Fechar
            </span>
          </div>
        </Box>
      </Modal>

      <DetalhesDescarteDialog
        open={detalhesDescarteOpen}
        handleClose={() => {
          setDetalhesDescarteOpen(false);
          setDescarteAtual(null);
        }}
        detalhes={descarteAtual}
      />
    </>
  );
}

export default ModalHistoricoMovimentos;
